/**
 * Utility for persisting task creation data when unauthenticated users
 * attempt to create a task. Data is saved to localStorage and restored
 * after successful signup.
 */

const STORAGE_KEY = "pending_task_creation";

export interface PendingTaskData {
	instructions: string;
	schedule: string; // "now", "daily", "weekly", "monthly", "interval"
	cronExpression?: string;
	permissionMode?: "yolo" | "safe" | "plan";
	selectedTools: string[];
	selectedIntegrationIds: number[];
	inboundEmailEnabled?: boolean;
}

/**
 * Save task creation data to localStorage
 */
export function savePendingTask(data: PendingTaskData): void {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
	} catch (error) {
		console.error("Failed to save pending task:", error);
	}
}

/**
 * Retrieve pending task data from localStorage
 */
export function getPendingTask(): PendingTaskData | null {
	try {
		const data = localStorage.getItem(STORAGE_KEY);
		if (!data) return null;

		const parsed = JSON.parse(data);

		// Validate required fields
		if (
			!parsed.instructions ||
			!parsed.schedule ||
			!Array.isArray(parsed.selectedTools) ||
			!Array.isArray(parsed.selectedIntegrationIds)
		) {
			console.warn("Invalid pending task data, clearing storage");
			clearPendingTask();
			return null;
		}

		return parsed as PendingTaskData;
	} catch (error) {
		console.error("Failed to retrieve pending task:", error);
		clearPendingTask();
		return null;
	}
}

/**
 * Clear pending task data from localStorage
 */
export function clearPendingTask(): void {
	try {
		localStorage.removeItem(STORAGE_KEY);
	} catch (error) {
		console.error("Failed to clear pending task:", error);
	}
}
